<script setup>
import { ref, onMounted, watch } from 'vue';
import { Toast } from 'bootstrap';

const props = defineProps({
  type: {
    type: String,
    default: 'success',
    validator: value =>
      ['success', 'info', 'warning', 'danger'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  autoHide: {
    type: Boolean,
    default: true,
  },
  delay: {
    type: Number,
    default: 5000,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['hide', 'show']);
const toastElement = ref(null);
const toastInstance = ref(null);
const isVisible = ref(props.visible);

// Initialize toast instance
onMounted(() => {
  if (toastElement.value) {
    toastInstance.value = new Toast(toastElement.value, {
      autohide: props.autoHide,
      delay: props.delay,
    });

    // Add event listeners
    toastElement.value.addEventListener('hidden.bs.toast', () => {
      isVisible.value = false;
      emit('hide');
    });

    toastElement.value.addEventListener('shown.bs.toast', () => {
      emit('show');
    });

    // Show toast if initially visible
    if (props.visible) {
      show();
    }
  }
});

// Watch for visible prop changes
watch(
  () => props.visible,
  newValue => {
    if (newValue) {
      show();
    } else {
      hide();
    }
  }
);

// Public methods
const show = () => {
  if (toastInstance.value) {
    toastInstance.value.show();
    isVisible.value = true;
  }
};

const hide = () => {
  if (toastInstance.value) {
    toastInstance.value.hide();
  }
};

// Expose methods to parent components
defineExpose({
  show,
  hide,
});

// Get icon based on type
const getIcon = type => {
  switch (type) {
    case 'success':
      return 'circle-check';
    case 'info':
      return 'info-circle';
    case 'warning':
      return 'triangle-exclamation';
    case 'danger':
      return 'circle-exclamation';
    default:
      return 'info-circle';
  }
};
</script>

<template>
  <div
    ref="toastElement"
    class="toast"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
  >
    <div class="toast-header" :class="`bg-${type} bg-opacity-25`">
      <i :class="`fa fa-solid fa-${getIcon(type)} me-2 text-${type}`" />
      <strong class="me-auto">{{ title }}</strong>
      <small>{{ new Date().toLocaleTimeString() }}</small>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="toast"
        :aria-label="$t('common.close')"
      />
    </div>
    <div class="toast-body">{{ message }}</div>
  </div>
</template>
